import React, { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronsRight, ChevronsLeft, ChevronRight } from "lucide-react";
import StackLean from "./stack-lean";
import StackUnitLoadHeight from "./stack-unit-load-height";
import Footer from "../footer";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/redux/store";
import { setActivePallet, } from "@/redux/slices/pallet/palletSlice";
import Rql from "../common-components/Rql";
import { Skeleton } from "@/components/ui/skeleton";



interface PostStackProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
}
  // Reusable component for content skeletons
  const TableContentSkeleton = () => (
    <div className="space-y-6 w-full animate-pulse">
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Table header skeleton */}
      <div className="border rounded-md overflow-hidden">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-6 p-3">
            <Skeleton className="h-6 w-full mx-2" />
            {Array(5).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>

        {/* Table rows skeleton */}
        <div className="p-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="grid grid-cols-6 gap-2 p-2 border-b last:border-0">
              <Skeleton className="h-10 w-full" />
              {Array(5).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons skeleton */}
      <div className="flex justify-end space-x-3">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  );
export default function PostStack({
  projectId,
  isOffline,
  onOfflineSubmit
}: PostStackProps) {
  const { activePallet, palletTypes, activeWeek } = useSelector((state: RootState) => state.pallets);
  const dispatch = useDispatch();
  const [expandedTabs, setExpandedTabs] = useState<{ [key: string]: boolean }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTabContentLoading, setIsTabContentLoading] = useState(false);
  const [activeStackHeightWeek, setActiveStackHeightWeek] = useState<null | string>(null)
  const paths = [
    // {
    //   id: 1,
    //   tabName: "Stack Test Layout",
    //   tabContent: <StackTestLayout />,
    // },
    { id: 2, displayTabName: "Post-Stack Lean", tabName: "Stack Lean", tabContent: <StackLean variant='postStack' setIsLoading={setIsLoading} /> },

    {
      id: 3,
      displayTabName: "Post-Stack Height",
      tabName: "Stack Unit Load Height",
      tabContent: <StackUnitLoadHeight variant="postStack" setIsLoading={setIsLoading} />,
    },
    { id: 4, displayTabName: "Post-Stack RQL", tabName: "Stack RQL", tabContent: <Rql variant="postStack" setIsLoading={setIsLoading} /> },
  ];

  const [selectedTab, setSelectedTab] = useState(paths[0].tabName);
  const [selectedPallet, setSelectedPallet] = useState(palletTypes[0] && palletTypes[0]?.id?.toString() || "");
  const [isNav, setIsNav] = useState(true);

  useEffect(() => {
    if (isOffline) {
      setIsEditing(true)
    }
  }, [isOffline])

  useEffect(() => {
    if (!isOffline) {
      setIsEditing(false)
    }
  }, [selectedTab])

  useEffect(() => {
    if (palletTypes.length > 0) {
      setSelectedPallet(palletTypes[0] && palletTypes[0]?.id?.toString() || "");
      dispatch(setActivePallet(palletTypes[0]));

      // Set the first tab as expanded
      setExpandedTabs({ [paths[0].tabName]: true });
    }

    // Simulate initial loading - replace with actual loading state in production
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [palletTypes, dispatch]);

  const handleFormChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  const toggleTabExpansion = (tabName: string) => {
    setExpandedTabs(prev => ({ ...prev, [tabName]: !prev[tabName] }));
  };

  const handleTabSelection = async (tabName: string, palletId: string) => {
    setIsTabContentLoading(true);
    if (hasUnsavedChanges) {
      await saveCurrentForm({ fromTab: true });
    }
    setSelectedTab(tabName);
    setSelectedPallet(palletId);
    dispatch(setActivePallet(palletTypes.find(p => p.id?.toString() === palletId) || null));

    // Simulate tab content loading - replace with actual loading state in production
    setTimeout(() => {
      setIsTabContentLoading(false);
    }, 500);
  };

  const saveCurrentForm = useCallback(async ({ fromTab = false }) => {
    if (activePallet?.id) {
      const stackLeanTable = document.getElementById(`stack-lean-table-${activePallet.id}`);
      if (stackLeanTable) {
        const submitButton = stackLeanTable.querySelector('button[type="submit"]');
        if (submitButton) {
          (submitButton as HTMLButtonElement).click();
        }
      }
      const unitLoadHeightTable = document.getElementById(`stack-unit-load-height-table-${activePallet?.id}`);
      if (unitLoadHeightTable) {
        const submitButton = unitLoadHeightTable.querySelector('button[type="submit"]');
        if (submitButton) {
          (submitButton as HTMLButtonElement).click();
        }
      }
      const rqlTable = document.getElementById(`rql-table-${activePallet?.id}`);
      if (rqlTable) {
        const submitButton = rqlTable.querySelector('button[type="submit"]');
        if (submitButton) {
          (submitButton as HTMLButtonElement).click();
        }
      }
    }

    const stackTest = document.getElementById(`stack-layout-test`);
    if (stackTest) {
      const submitButton = stackTest.querySelector('button[type="submit"]');
      if (submitButton) {
        (submitButton as HTMLButtonElement).click();
      }
    }
    setHasUnsavedChanges(false);
  }, [activePallet, selectedTab, hasUnsavedChanges, activeWeek]);




  if (isInitialLoading) {
    return (
      <div className="w-full flex h-[calc(100vh-140px)]">
        {/* Sidebar skeleton */}
        <div className="w-[200px] h-full bg-light-blue flex flex-col items-start justify-start pt-10 overflow-y-auto">
          {paths.map((path, index) => (
            <div key={index} className="w-full mb-4 px-4">
              <Skeleton className="h-12 w-full rounded-md" />
              {index === 0 && (
                <div className="pl-8 py-2 space-y-3 mt-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-md" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main content skeleton */}
        <div className="flex-1 overflow-hidden">
          <div className="p-8">
            <TableContentSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100vh-140px)]">
      <Tabs
        defaultValue={`${paths[0].tabName}-${palletTypes[0]?.id}`}
        className="w-full flex"
        value={`${selectedTab}-${selectedPallet}`}
        onValueChange={(value) => {
          const [tabName, palletId] = value.split('-');
          handleTabSelection(tabName, palletId);
        }}
      >
        {isNav ? (
          <TabsList className="w-[200px] relative z-50 h-full bg-light-blue flex flex-col justify-start py-8 overflow-y-auto">
            <ChevronsLeft onClick={() => setIsNav(!isNav)} className="cursor-pointer absolute top-2 right-2 text-blue" />
            {paths.map((path) => (
              <div key={path.id} className="w-full mb-2">
                <TabsTrigger
                  value={`${path.tabName}-${activePallet?.id}`}
                  className={cn(
                    "w-full justify-between px-6 py-4 text-left text-lg font-semibold",
                    selectedTab === path.tabName ? "bg-bgBlue text-blue" : ""
                  )}
                  onClick={() => toggleTabExpansion(path.tabName)}
                >
                  <span>{path.displayTabName}</span>
                  {expandedTabs[path.tabName] ? <ChevronDown className="text-blue min-h-6 min-w-6" /> : <ChevronRight className=" min-h-6 min-w-6" />}
                </TabsTrigger>
                {expandedTabs[path.tabName] && (
                  <div className="pl-8 py-2 bg-white">
                    {palletTypes.map((pallet) => (
                      <TabsTrigger
                        key={`${path.tabName}-${pallet.id}`}
                        value={`${path.tabName}-${pallet.id}`}
                        className={cn(
                          "w-full justify-start px-4 py-2 text-sm",
                          selectedTab === path.tabName && selectedPallet === pallet.id?.toString()
                            ? "bg-blue/20 text-blue font-semibold"
                            : "hover:bg-gray-100"
                        )}
                        onClick={() => handleTabSelection(path.tabName, pallet.id?.toString() || "")}
                      >
                        <div className="flex flex-col gap-1">
                          <div>{pallet.variant}</div>
                          <div className="">{pallet.name}</div>
                        </div>
                      </TabsTrigger>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </TabsList>
        ) : (
          <TabsList className="w-[40px] relative z-50 h-full bg-light-blue flex flex-col items-start justify-start pt-10 overflow-y-auto">
            <ChevronsRight onClick={() => setIsNav(!isNav)} className="cursor-pointer absolute top-2 right-2 text-blue" />
          </TabsList>
        )}
        <div className="flex-1 overflow-y-auto pb-[90px]">
      
          {paths.map((path) => (
            palletTypes.map((pallet) => (
              <TabsContent
                key={`${path.tabName}-${pallet.id}`}
                value={`${path.tabName}-${pallet.id}`}
                className={cn(
                  " z-[-1000]  overflow-y-auto p-3 ",
                  path.tabName !== "Ship Test Truck Layout" && "space-y-4"
                )}
              >
                {isTabContentLoading && selectedTab === path.tabName && selectedPallet === pallet.id?.toString() ? (
                  <TableContentSkeleton />
                ) : (
                  React.cloneElement(path.tabContent as React.ReactElement, {
                    palletId: pallet.id,
                    isEditing,
                    activeWeek:activeStackHeightWeek,
                    setActiveWeek:setActiveStackHeightWeek,
                    onFormChange: handleFormChange,
                    tabName: path.tabName,
                    stage: 'postStack',
                    isOffline,
                    setIsLoading,
                    onOfflineSubmit: async (values: any) => {
                      if (isOffline && onOfflineSubmit) {
                        await onOfflineSubmit({
                          type: 'postStack',
                          component: path.tabName,
                          palletId: pallet.id,
                          data: values
                        });
                      }
                    }
                  })
                )}
              </TabsContent>
            ))
          ))}
        </div>
      </Tabs>
      <Footer
        layout
        isLoading={isLoading}
        onSubmit={() => saveCurrentForm({ fromTab: false })}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </div>
  );
}
